// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import setIsUpdatingCheckout from "@bg/App/Handlers/OnePageCheckout/SetIsUpdatingCheckout";
import { CustomAppState } from "@bg/App/Store/Reducers";
import {
    addToChainBefore,
    addToEndOfChain,
    addToStartOfChain,
    makeHandlerChainAwaitable,
    replaceInChain,
} from "@insite/client-framework/HandlerCreator";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import validateAddress, { AddressErrors } from "@insite/client-framework/Store/CommonHandlers/ValidateAddress";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCart";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import {
    chain,
    DispatchCompleteUpdateShipTo,
    LoadCart,
} from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/UpdateShipTo";

const DispatchStartCheckoutLoading: typeof DispatchCompleteUpdateShipTo = props => {
    props.dispatch(setIsUpdatingCheckout({ isUpdatingCheckout: true }));
};

export const DispatchSetLastSelectedShipTo: typeof DispatchCompleteUpdateShipTo = props => {
    const state = props.getState();
    const {
        xOnePageCheckout: { useOneTimeAddress },
    } = state as CustomAppState;
    if (useOneTimeAddress) {
        return;
    }

    const shipToState = getShipToState(state, props.apiResult.id);

    props.dispatch({
        type: "xOnePageCheckout/SetLastSelectedShipTo" as any,
        shipTo: shipToState.value!,
    });

    props.dispatch({
        type: "xOnePageCheckout/SetUseOneTimeAddress" as any,
        useOneTimeAddress: false,
    });
};

export const ValidateShippingAddress: typeof DispatchCompleteUpdateShipTo = async ({ dispatch, getState }) => {
    const state = getState();

    const { cartId } = state.pages.onePageCheckout;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    if (!cart || !cart.billToId) {
        return;
    }

    const shipTo = getShipToState(state, cart.shipToId).value;
    if (!shipTo) {
        return;
    }

    if (cart.fulfillmentMethod === FulfillmentMethod.PickUp) {
        return;
    }

    let addressFieldsDataView = getAddressFieldsDataView(state);
    if (!addressFieldsDataView.value) {
        addressFieldsDataView = getAddressFieldsDataView(state);
    }

    if (!shipTo.validation) {
        return;
    }

    const awaitableValidate = makeHandlerChainAwaitable<Parameters<typeof validateAddress>[0], AddressErrors>(
        validateAddress,
    );
    const shippingAddressErrors = await awaitableValidate({
        address: shipTo,
        validationRules: shipTo.validation!,
        addressFieldDisplayCollection: addressFieldsDataView.value!.shipToAddressFields,
    })(dispatch, getState);
    const isShippingAddressValid = Object.keys(shippingAddressErrors).length === 0;

    dispatch({
        type: "Pages/OnePageCheckout/SetIsShippingAddressUpdateRequired",
        isShippingAddressUpdateRequired: !isShippingAddressValid,
    });
    dispatch({
        type: "xOnePageCheckout/SetUseOneTimeAddress" as any,
        useOneTimeAddress: !isShippingAddressValid,
    });
};

const DispatchFinishCheckoutLoading: typeof DispatchCompleteUpdateShipTo = props => {
    props.dispatch(setIsUpdatingCheckout({ isUpdatingCheckout: false }));
};

export const XngLoadCart: typeof LoadCart = async props => {
    const state = props.getState();
    const { cartId } = state.pages.onePageCheckout;
    if (cartId) {
        props.dispatch(loadCart({ cartId, shouldLoadFullCart: true, getPromotions: true }));
        return;
    }
    await makeHandlerChainAwaitable(loadCurrentCart)({ getCartLines: true, getPromotions: true })(
        props.dispatch,
        props.getState,
    );
};

replaceInChain(chain, LoadCart, XngLoadCart);
addToStartOfChain(chain, DispatchStartCheckoutLoading);
addToChainBefore(chain, DispatchCompleteUpdateShipTo, DispatchSetLastSelectedShipTo);
addToChainBefore(chain, DispatchSetLastSelectedShipTo, ValidateShippingAddress);
addToEndOfChain(chain, DispatchFinishCheckoutLoading);
